'use client';

import type { CustomAgentChangeProposal } from '@/app/(chat)/api/chat/chat-tools/custom-agent-tools';
import type { CustomRule } from '@/lib/custom-rules/service';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useInteractiveProposalActionState } from './interactive-proposal-state';
import { createCustomRuleRequest, fetchCustomRules, updateCustomRuleRequest } from '@/data/custom-rules/api';
import { customRulesQueryKey, hasReachedEnabledRulesLimit } from '@/data/custom-rules/use-custom-rules';
import { installationCustomRulesQueryKey } from '@/data/custom-rules/use-installation-custom-rules';
import { getCustomRuleOwner } from '@/lib/custom-rules/rule-owner';
type UseCustomAgentChangeProposalActionResult = {
  action: ReturnType<typeof useInteractiveProposalActionState>;
  handleApply: () => Promise<void>;
  handleCancel: () => void;
};
function isExactCreateAlreadyApplied(proposal: CustomAgentChangeProposal, rule: CustomRule): boolean {
  const after = proposal.after;
  return proposal.action === 'create' && rule.repositoryId === proposal.repository.repositoryId && rule.title === after.title && rule.description === after.description && rule.enabled === after.enabled && rule.includePatterns === after.includePatterns && rule.excludePatterns === after.excludePatterns && rule.category === after.category;
}
function customAgentChangeSuccessMessage(proposal: CustomAgentChangeProposal) {
  return proposal.action === 'create' ? 'Custom agent created' : 'Custom agent updated';
}
function customAgentChangeToastId(proposal: CustomAgentChangeProposal) {
  const agentKey = proposal.agentId ?? proposal.after.title;
  return `custom-agent-change:${proposal.repository.installationId}:${proposal.repository.repositoryId}:${proposal.action}:${agentKey}`;
}
export function useCustomAgentChangeProposalAction(proposal: CustomAgentChangeProposal): UseCustomAgentChangeProposalActionResult {
  const queryClient = useQueryClient();
  const action = useInteractiveProposalActionState();
  const invalidateCustomRules = useCallback(async () => {
    await Promise.all([queryClient.invalidateQueries({
      queryKey: customRulesQueryKey(proposal.repository.installationId, proposal.repository.repositoryId)
    }), queryClient.invalidateQueries({
      queryKey: customRulesQueryKey(proposal.repository.installationId)
    }), queryClient.invalidateQueries({
      queryKey: installationCustomRulesQueryKey(proposal.repository.installationId)
    })]);
  }, [proposal.repository.installationId, proposal.repository.repositoryId, queryClient]);
  const handleApply = useCallback(async () => {
    action.beginApplying();
    const toastId = customAgentChangeToastId(proposal);
    let showedOptimisticSuccessToast = false;
    try {
      if (proposal.action === 'create') {
        const latest = await fetchCustomRules(proposal.repository.installationId, proposal.repository.repositoryId);
        // A model response can be replayed after reload. Treat an exact
        // title+description match as already applied so Apply stays idempotent.
        const existingRule = latest.rules.find(rule => isExactCreateAlreadyApplied(proposal, rule));
        if (existingRule) {
          await invalidateCustomRules();
          action.markApplied('Already applied.');
          toast.success('Custom agent already exists');
          return;
        }
        if (proposal.after.enabled && hasReachedEnabledRulesLimit(latest.rules, latest.maxEnabledRules)) {
          throw new Error('The enabled custom-agent limit has been reached. Ask chat for a disabled proposal.');
        }
        toast.success(customAgentChangeSuccessMessage(proposal), {
          id: toastId
        });
        showedOptimisticSuccessToast = true;
        await createCustomRuleRequest(proposal.repository.installationId, proposal.applyPayload);
      } else {
        const latest_0 = await fetchCustomRules(proposal.repository.installationId, proposal.repository.repositoryId);
        const currentRule = latest_0.rules.find(rule_0 => rule_0.id === proposal.agentId);
        if (!currentRule) {
          throw new Error('This custom agent no longer exists.');
        }
        const owner = getCustomRuleOwner(currentRule);
        if (owner === 'yaml') {
          throw new Error('This custom agent is managed by YAML and cannot be updated here.');
        }
        if (owner === 'organization') {
          throw new Error('This custom agent runs on several repositories. Edit it in the Agents library.');
        }
        if (!currentRule.enabled && proposal.after.enabled && hasReachedEnabledRulesLimit(latest_0.rules, latest_0.maxEnabledRules)) {
          throw new Error('The enabled custom-agent limit has been reached.');
        }
        toast.success(customAgentChangeSuccessMessage(proposal), {
          id: toastId
        });
        showedOptimisticSuccessToast = true;
        await updateCustomRuleRequest(proposal.repository.installationId, proposal.agentId, proposal.applyPayload);
      }
      await invalidateCustomRules();
      action.markApplied('Applied.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to apply custom agent change';
      action.markError(message);
      toast.error(message, showedOptimisticSuccessToast ? {
        id: toastId
      } : undefined);
    }
  }, [action, invalidateCustomRules, proposal]);
  const handleCancel = useCallback(() => {
    action.markCancelled('Cancelled.');
  }, [action]);
  return {
    action,
    handleApply,
    handleCancel
  };
}